import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, Eye, ExternalLink, History, Monitor, RotateCcw, Rocket, Save, Smartphone, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth, can } from "@/lib/auth";
import { fmtDate } from "@/lib/format";
import { norm, PAGE_KEYS, PAGE_NAMES, PAGE_ROUTES } from "@/lib/siteIndex";
import { PageTitle, Loader, Section, Empty } from "@/components/xd/ui";
import { RichEditor } from "@/components/xd/RichEditor";
import { useAct, ActionDialog } from "./Admin";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const LANG_LABEL = { he: "עברית", ar: "العربية" };

const Meta = ({ label, m }) => (m ? <span className="text-zinc-500 ltr-num">{label}: {fmtDate(m.at)} · {m.by_name || m.by_uid}</span> : null);

export const Preview = ({ html, lang, open, onClose, title }) => {
  const { t } = useLang();
  const [mobile, setMobile] = useState(false);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl w-[96vw] max-h-[92vh] overflow-hidden flex flex-col" data-testid="page-preview-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          <button type="button" className={mobile ? "btn-ghost" : "btn-xd"} onClick={() => setMobile(false)} data-testid="page-preview-desktop"><Monitor size={15} />{t("pe.desktop")}</button>
          <button type="button" className={mobile ? "btn-xd" : "btn-ghost"} onClick={() => setMobile(true)} data-testid="page-preview-mobile"><Smartphone size={15} />{t("pe.mobile")}</button>
        </div>
        <div className="overflow-y-auto flex-1 bg-black border border-white/10">
          <div className={`mx-auto transition-[max-width] ${mobile ? "max-w-[390px] border-x border-white/10" : "max-w-3xl"} px-5 py-8`} data-testid="page-preview-frame">
            <div className="xd-prose" dir={lang === "ar" || lang === "he" ? "rtl" : "ltr"} dangerouslySetInnerHTML={{ __html: html }} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Revisions = ({ pkey, lang, reloadKey, onRestore, canEdit }) => {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  const [view, setView] = useState(null);
  useEffect(() => { api.get(`/admin/pages/${pkey}/${lang}/revisions`).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, [pkey, lang, reloadKey]);
  if (!items) return <Loader />;
  return (
    <div className="space-y-2" data-testid="page-revisions">
      {items.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 pb-2 text-xs" data-testid={`page-revision-${r.id}`}>
          <span className="min-w-0"><span className={`px-1.5 py-0.5 border me-2 ${r.kind === "published" ? "border-emerald-700 text-emerald-300" : "border-white/15 text-zinc-300"}`}>{t(`pe.kind.${r.kind}`)}</span>
            <span className="text-zinc-400 ltr-num">{fmtDate(r.at)} · {r.by_name || r.by_uid} · {r.html.length.toLocaleString()} {t("pe.chars")}</span></span>
          <span className="flex gap-2">
            <button type="button" className="btn-ghost !min-h-[2rem] text-xs" onClick={() => setView(r)} data-testid={`page-revision-view-${r.id}`}><Eye size={13} />{t("pe.view_revision")}</button>
            {canEdit && <ActionDialog testid={`page-revision-restore-${r.id}`} title={t("pe.restore_confirm")} onSubmit={() => onRestore(r.id)} trigger={<button type="button" className="btn-ghost !min-h-[2rem] text-xs" data-testid={`page-revision-restore-button-${r.id}`}><RotateCcw size={13} />{t("pe.restore")}</button>} />}
          </span>
        </div>
      ))}
      {view && <Preview open html={view.html} lang={lang} title={`${t(`pe.kind.${view.kind}`)} · ${fmtDate(view.at)}`} onClose={() => setView(null)} />}
    </div>
  );
};

const highlight = (editor, q) => {
  const nq = norm(q);
  let hit = null;
  editor.state.doc.descendants((node, pos) => {
    if (hit || !node.isText) return !hit;
    const i = node.text.toLowerCase().indexOf(q.toLowerCase());
    const j = i < 0 ? norm(node.text).indexOf(nq) : i;
    if (j >= 0) hit = { from: pos + j, to: pos + j + q.length };
    return !hit;
  });
  if (hit) { editor.chain().focus().setTextSelection(hit).scrollIntoView().run(); }
};

export default function PageEditor() {
  const { key } = useParams();
  const [params, setParams] = useSearchParams();
  const lang = params.get("lang") === "ar" ? "ar" : "he";
  const q = params.get("q");
  const { t, lang: ui } = useLang();
  const { user } = useAuth();
  const act = useAct();
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);
  const [html, setHtml] = useState("");
  const [base, setBase] = useState("");
  const [preview, setPreview] = useState(false);
  const [rev, setRev] = useState(0);
  const edRef = useRef(null);
  const load = () => api.get(`/admin/pages/${key}/${lang}`).then((r) => setD(r.data)).catch(() => setDenied(true));
  useEffect(() => { setD(null); load(); }, [key, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!PAGE_KEYS.includes(key) || denied) return <Empty text={t("err.forbidden")} />;
  if (!d) return <Loader />;
  const canEdit = can(user, "content.manage");
  const canPub = can(user, "content.publish");
  const dirty = html !== base;
  const onReady = (ed) => { edRef.current = ed; const h = ed.getHTML(); setHtml(h); setBase(h); if (q) { setTimeout(() => highlight(ed, q), 200); } };
  const switchLang = (l) => { if (l === lang || (dirty && !window.confirm(t("pe.unsaved_switch")))) return; setParams({ lang: l }); };
  const save = async () => { const r = await act(() => api.put(`/admin/pages/${key}/${lang}/draft`, { html })); if (r) { setD(r.data); setBase(html); setRev((x) => x + 1); } return r; };
  const publish = async () => { if (dirty && !(await save())) return; const r = await act(() => api.post(`/admin/pages/${key}/${lang}/publish`)); if (r) { setD(r.data); setRev((x) => x + 1); } };
  const restore = async (rid) => { const r = await act(() => api.post(`/admin/pages/${key}/${lang}/revisions/${rid}/restore`)); if (r) { setD(r.data); edRef.current?.commands.setContent(r.data.draft); const h = edRef.current?.getHTML() || r.data.draft; setHtml(h); setBase(h); setRev((x) => x + 1); } };
  const name = PAGE_NAMES[key][ui] || PAGE_NAMES[key].he;
  return (
    <div className="space-y-6" data-testid={`page-editor-${key}`}>
      <Link to="/admin/site?section=pages" className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1" data-testid="page-editor-back"><ArrowRight size={13} />{t("se.back")}</Link>
      <PageTitle eyebrow={t("pe.title")} title={name}>
        <div className="flex flex-wrap gap-2">
          {canEdit && <button className="btn-ghost" disabled={!dirty} onClick={save} data-testid="page-save-draft-button"><Save size={15} />{t("pe.save_draft")}</button>}
          <button className="btn-ghost" onClick={() => setPreview(true)} data-testid="page-preview-button"><Eye size={15} />{t("pe.preview")}</button>
          <a className="btn-ghost" href={`${PAGE_ROUTES[key]}?lang=${lang}`} target="_blank" rel="noreferrer" data-testid="page-view-button"><ExternalLink size={15} />{t("pe.view_page")}</a>
          {canPub && <ActionDialog testid="page-publish" title={`${t("pe.publish_confirm")} ${t("pe.publish_note")}`} onSubmit={publish} trigger={<button className="btn-xd" data-testid="page-publish-button"><Rocket size={15} />{t("pe.publish")}</button>} />}
        </div>
      </PageTitle>
      <div className="flex flex-wrap gap-2" data-testid="page-lang-tabs">
        {["he", "ar"].map((l) => <button key={l} type="button" className={l === lang ? "btn-xd" : "btn-ghost"} onClick={() => switchLang(l)} data-testid={`page-lang-${l}`}>{LANG_LABEL[l]}</button>)}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs" data-testid="page-status">
        <span className={`px-2 py-1 border ${dirty ? "border-amber-500 text-amber-300" : "border-white/10 text-zinc-400"}`} data-testid="page-status-dirty">{dirty ? t("pe.unsaved") : t("pe.saved")}</span>
        <span className={`px-2 py-1 border ${d.has_unpublished ? "border-amber-500 text-amber-300" : "border-emerald-700 text-emerald-300"}`} data-testid="page-status-live">{d.has_unpublished ? t("pe.differs") : t("pe.matches")}</span>
        <Meta label={t("pe.draft_meta")} m={d.draft_meta} />
        <Meta label={t("pe.published_meta")} m={d.published_meta} />
      </div>
      {key !== "about" && <p className="text-xs text-amber-300/90 flex items-start gap-2" data-testid="page-legal-draft-note"><ShieldAlert size={15} className="shrink-0 mt-0.5" />{t("pe.legal_draft")}</p>}
      <p className="text-[11px] text-zinc-500">{t("pe.publish_note")}</p>
      <RichEditor key={`${key}-${lang}-${d.draft_meta?.at}`} html={d.draft} dir="rtl" editable={canEdit} onChange={setHtml} onReady={onReady} />
      <Section title={<span className="inline-flex items-center gap-2"><History size={16} />{t("pe.revisions")} · {LANG_LABEL[lang]}</span>} testid="page-revisions-section">
        <Revisions pkey={key} lang={lang} reloadKey={rev} onRestore={restore} canEdit={canEdit} />
      </Section>
      {preview && <Preview open html={html} lang={lang} title={`${name} · ${LANG_LABEL[lang]}`} onClose={() => setPreview(false)} />}
    </div>
  );
}
